import React, { useState, useEffect, useRef } from 'react';
import { getSports, type PlatformSport } from '../services/sports';

export type SportTypeSelectProps = {
  value: string | string[];
  onChange: (value: string[]) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  multiple?: boolean;
  className?: string;
};

export default function SportTypeSelect({
  value,
  onChange,
  label,
  placeholder = 'Select sports...',
  disabled = false,
  multiple = true,
  className = '',
}: SportTypeSelectProps) {
  const [sports, setSports] = useState<PlatformSport[]>([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const selectedValues = Array.isArray(value) ? value : value ? [value] : [];

  // Filter sports based on search term
  const filteredSports = sports.filter(sport =>
    sport.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    async function loadSports() {
      try {
        const data = await getSports();
        setSports(data);
      } catch (err) {
        console.error('Failed to load sports:', err);
      } finally {
        setLoading(false);
      }
    }
    loadSports();
  }, []);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  const toggleOption = (sport: PlatformSport) => {
    // Unticking has to remove whichever spelling is stored — the id or the
    // legacy name — or the chip would stay behind.
    const without = selectedValues.filter(v => !matches(sport, v));
    if (multiple) {
      if (without.length !== selectedValues.length) onChange(without);
      else onChange([...selectedValues, sport.name]);
    } else {
      onChange([sport.name]);
      setIsOpen(false);
    }
  };

  /**
   * A stored value may be a sport ID or, on older rows, its name. Matching only
   * on the name made an existing selection look empty — the club's sports were
   * there, the control just did not recognise them.
   */
  const matches = (sport: PlatformSport, value: string) => sport.id === value || sport.name === value;
  const isSelected = (sport: PlatformSport) => selectedValues.some(v => matches(sport, v));
  const selectedLabels = sports.filter(isSelected).map(s => s.name);
  const btnClass = 'w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-left focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ' + (disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer');
  const svgClass = 'absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 transition-transform ' + (isOpen ? 'rotate-180' : '');

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-slate-700 mb-2">{label}</label>
      )}
      <div ref={containerRef} className="relative">
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className={btnClass}
        >
          {loading ? (
            <span className="text-slate-400">Loading...</span>
          ) : selectedLabels.length > 0 ? (
            <span className="text-slate-700">{selectedLabels.join(', ')}</span>
          ) : (
            <span className="text-slate-400">{placeholder}</span>
          )}
          <svg className={svgClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {isOpen && !loading && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden">
            {/* Search input */}
            <div className="p-2 border-b border-slate-100">
              <div className="relative">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search sports..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-sm text-slate-800 bg-slate-50 border border-slate-200 rounded-lg placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </div>
            <div className="max-h-48 overflow-auto">
              {/* Clear option for single-select mode */}
              {!multiple && selectedValues.length > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    onChange([]);
                    setIsOpen(false);
                    setSearchTerm('');
                  }}
                  className="w-full flex items-center px-4 py-2 hover:bg-red-50 cursor-pointer text-red-600 border-b border-slate-100"
                >
                  <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  <span className="text-sm">Clear selection</span>
                </button>
              )}
              {filteredSports.length === 0 ? (
                <div className="px-4 py-3 text-slate-500 text-sm text-center">
                  {sports.length === 0 ? 'No sports available' : 'No sports match your search'}
                </div>
              ) : (
                filteredSports.map(sport => (
                  <label key={sport.id} className="flex items-center px-4 py-2 hover:bg-slate-50 cursor-pointer">
                    <input
                      type={multiple ? 'checkbox' : 'radio'}
                      checked={isSelected(sport)}
                      onChange={() => toggleOption(sport)}
                      className="w-4 h-4 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500"
                    />
                    <span className="ml-3 text-sm text-slate-700">{sport.name}</span>
                  </label>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
